import {
  Barcode,
  BarcodeBatch,
  BarcodeBatchListener,
  BarcodeBatchSession,
  BarcodeCapture,
  BarcodeCaptureListener,
  BarcodeCaptureSession,
} from '@scandit/web-datacapture-barcode'

import { BarcodeListStore } from './BarcodeListStore'
import CameraStore, { TCameraStore } from './CameraStore'
import ErrorsStore, { TErrorsStore } from './ErrorsStore'
import InitialOptionsStore, { ScannerMode, TInitialOptionsStore } from './InitialOptionsStore'
import { BarcodeBatchSettingsStore, BarcodeCaptureSettingsStore, SettingsStore } from './SettingsStore'
import SingleImageModeStore, { TSingleImageModeStore } from './SingleImageModeStore'

import type EventTracker from './EventTracker'
import { LoadingStore, TLoadingStore } from './LoadingStore'
import { SDK } from './sdk'

class CaptureListener implements BarcodeCaptureListener {
  private readonly notify: (session: BarcodeCaptureSession) => void
  constructor(notify: (session: BarcodeCaptureSession) => void) {
    this.notify = notify
  }
  didScan(_barcodeCaptureMode: BarcodeCapture, session: BarcodeCaptureSession) {
    this.notify(session)
  }
}

class BatchListener implements BarcodeBatchListener {
  private readonly notify: (session: BarcodeBatchSession) => void
  constructor(notify: (session: BarcodeBatchSession) => void) {
    this.notify = notify
  }
  didUpdateSession(_barcodeBatchMode: BarcodeBatch, session: BarcodeBatchSession) {
    this.notify(session)
  }
}

class AppStore {
  public sdk: SDK | undefined = undefined
  public tracker: EventTracker | undefined

  public initialOptionsStore: TInitialOptionsStore
  public settingsStore!: SettingsStore
  public cameraStore: TCameraStore

  public listStore?: BarcodeListStore

  public errorsStore: TErrorsStore
  public singleImageModeStore: TSingleImageModeStore
  public loadingStore: TLoadingStore
  private readonly captureListener: CaptureListener
  private readonly batchListener: BatchListener

  constructor() {
    this.initialOptionsStore = InitialOptionsStore(this)
    this.cameraStore = CameraStore(this)
    this.errorsStore = ErrorsStore(this)
    this.singleImageModeStore = SingleImageModeStore(this)
    this.loadingStore = LoadingStore(this)

    this.captureListener = new CaptureListener((session: BarcodeCaptureSession) => {
      const barcode: Barcode | null = session.newlyRecognizedBarcode
      if (barcode) {
        this.listStore?.add([barcode])
        this.tracker!.sendEvent('Web SDK', 'scan', barcode.symbology)
      }
    })
    this.batchListener = new BatchListener((session: BarcodeBatchSession) => {
      this.listStore?.updateFromTrackedBarcodes(Object.values(session.addedTrackedBarcodes))
    })
  }

  async launch() {
    const scannerMode = this.initialOptionsStore.scannerMode.peek()
    const colorInvertedEnabled = this.initialOptionsStore.colorInvertedValue.peek()

    if (scannerMode === ScannerMode.ExternalListBuilding || scannerMode === ScannerMode.ExternalSearchAndFind) {
      const sampleName =
        scannerMode === ScannerMode.ExternalSearchAndFind ? 'search-and-find-sample' : 'list-building-sample'
      window.open(`https://websdk6.scandit.io/public/${sampleName}/`, '_blank', 'noopener, noreferrer')
      return
    }

    this.initialOptionsStore.launched.value = true
    this.listStore = new BarcodeListStore()

    if (scannerMode === ScannerMode.Batch) {
      const { mode, settings } = (await this.sdk?.configureBatchMode()) ?? {}

      if (!mode || !settings) {
        throw new Error('Batch mode not configured')
      }
      this.settingsStore = new BarcodeBatchSettingsStore({
        mode,
        settings,
      })
      await this.settingsStore.init(scannerMode).setColorInverted(colorInvertedEnabled).apply()
      mode.addListener(this.batchListener)
    } else if (scannerMode != undefined) {
      const { mode, settings } = (await this.sdk?.configureCaptureMode()) ?? {}
      if (!mode || !settings) {
        throw new Error('Capture mode not configured')
      }
      this.settingsStore = new BarcodeCaptureSettingsStore({
        mode,
        settings,
      })

      await this.settingsStore.init(scannerMode).setColorInverted(colorInvertedEnabled).apply()
      mode.addListener(this.captureListener)
    }

    await this.sdk?.getCurrentMode()?.setEnabled(true)

    await this.cameraStore.fetchCameras()
    await this.cameraStore.selectDefaultCamera();
  }

  injectSDK(sdkInstance: SDK) {
    this.sdk = sdkInstance
  }

  injectTracker(tracker: EventTracker) {
    this.tracker = tracker
  }
}

export default AppStore
